import { ReactNode } from 'react'

import { Box, Paper, Typography } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'

interface DiagnosisColumnShellProps<TCard> {
  cards: readonly TCard[]
  emptyText: string
  /** Renders one card. `index === 0` is the top-priority card of the bucket. */
  renderCard: (card: TCard, index: number) => ReactNode
}

/**
 * Layout shell for a diagnosis bucket: a dashed empty-state Paper when there
 * are no cards, otherwise a two-up grid.
 *
 * Cards sit side by side from `sm` up, and a lone card keeps its single-column
 * (~50%) width rather than stretching full width, so one blind spot reads the
 * same size as it would beside a second.
 *
 * Shared by the single-game and comparison diagnosis columns, which are
 * intentional visual mirrors of each other.
 */
export const DiagnosisColumnShell = <TCard,>({ cards, emptyText, renderCard }: DiagnosisColumnShellProps<TCard>) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {cards.length === 0 ? (
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderStyle: 'dashed',
          borderColor: 'divider',
          backgroundColor: 'transparent',
          borderRadius: borderRadiusDefault,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          {emptyText}
        </Typography>
      </Paper>
    ) : (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2, alignItems: 'stretch' }}>
        {cards.map((card, index) => renderCard(card, index))}
      </Box>
    )}
  </Box>
)
